import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router'

import { getCurrentIsProduction } from '@/locale'
import { extractProductQueryString } from '@/utils/queryString'
import { buildRhinixUrl, resolveRhinixOrigin } from '@/utils/rhinixMigration'

import { Route } from './route'

// 自由拼貼相關頁面已移植到 rhinix（host）+ ditto-nuxt（layer），ditto-web 端一律轉導過去。
// 其餘頁面（gallery / artist / collage 列表…）此階段先按兵不動。
function getRhinixRedirectPath(to: RouteLocationNormalized): null | string {
  if (to.name === Route.DesignPage) {
    return '/ditto/customize'
  }
  if (to.name === Route.IpCollabDesignPage) {
    const displayId = to.params['displayId']
    return typeof displayId === 'string'
      ? `/ditto/ip-collab-design/${encodeURIComponent(displayId)}`
      : null
  }
  return null
}

const routes: RouteRecordRaw[] = [
  {
    path: Route.Root,
    name: Route.Root,
    redirect(to) {
      return { path: Route.GalleryPage, query: to.query }
    },
  },
  {
    path: Route.DesignPage,
    name: Route.DesignPage,
    component: async () => await import('@/pages/DesignPage.vue'),
  },
  {
    path: Route.IpCollabDesignPage,
    name: Route.IpCollabDesignPage,
    component: async () => await import('@/pages/ipCollabTemplatePage/IpCollabTemplatePage.vue'),
  },
  {
    path: Route.DesignArtistPage,
    name: Route.DesignArtistPage,
    component: async () => await import('@/pages/DesignArtistPage.vue'),
  },
  {
    path: Route.DevelopPage,
    name: Route.DevelopPage,
    component: async () => await import('@/pages/DevelopPage.vue'),
  },
  {
    path: Route.CheckoutPage,
    name: Route.CheckoutPage,
    component: async () => await import('@/pages/CheckoutPage.vue'),
    beforeEnter: (_to, from, next) => {
      if (
        from.name === Route.DesignArtistPage ||
        from.name === Route.DesignPage ||
        from.name === Route.IpCollabDesignPage
      ) {
        next()
      } else {
        next(Route.DesignPage)
      }
    },
  },
  {
    path: Route.ArtistPage,
    name: Route.ArtistPage,
    component: async () => await import('@/pages/ArtistPage.vue'),
  },
  {
    path: Route.GalleryPage,
    name: Route.GalleryPage,
    component: async () => await import('@/pages/GalleryPage.vue'),
  },
  {
    path: Route.StickerGroupPage,
    name: Route.StickerGroupPage,
    component: async () => await import('@/pages/productPage/StickerGroupPage.vue'),
  },
  {
    path: Route.BackgroundPage,
    name: Route.BackgroundPage,
    component: async () => await import('@/pages/productPage/BackgroundPage.vue'),
  },
  {
    path: Route.CollagePage,
    name: Route.CollagePage,
    component: async () => await import('@/pages/productPage/CollagePage.vue'),
  },
  {
    path: Route.ArtistsPage,
    name: Route.ArtistsPage,
    component: async () => await import('@/pages/ArtistsPage.vue'),
  },
  {
    path: Route.FavoriteArtworkPage,
    name: Route.FavoriteArtworkPage,
    component: async () => await import('@/pages/FavoriteArtworkPage.vue'),
  },
  {
    path: Route.StampDemoPage,
    name: Route.StampDemoPage,
    component: async () => await import('@/pages/StampDemoPage.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: Route.Root,
  },
]

export const router = createRouter({ routes, history: createWebHistory() })

// 手動掛一個 previousPath 屬性在 router
declare module 'vue-router' {
  interface Router {
    previousPath?: string
  }
}

router.beforeEach((to, from, next) => {
  // 自由拼貼頁轉導 rhinix customize；query 原樣帶過去（兩邊 query 契約已對齊）。
  const rhinixPath = getRhinixRedirectPath(to)
  if (rhinixPath !== null) {
    const origin = resolveRhinixOrigin(window.location)
    // 整頁導去 rhinix；full document navigation 自然中止這次 SPA 路由解析。
    window.location.replace(buildRhinixUrl(origin, rhinixPath, to.query))
    return
  }

  // 記錄上一個路徑
  if (from.path !== Route.Root.toString()) {
    router.previousPath = from.path
  }

  if (to.name === Route.StampDemoPage && getCurrentIsProduction()) {
    next(Route.GalleryPage)
    return
  }
  if (to.name === Route.DevelopPage && getCurrentIsProduction()) {
    next(Route.GalleryPage)
    return
  }
  if (extractProductQueryString(to.query) !== null) {
    next()
    return
  }

  const productQs = extractProductQueryString(from.query)
  if (productQs !== null) {
    next({
      ...to,
      query: {
        ...to.query,
        ...productQs,
      },
    })
    return
  }
  next()
})
